/**
 * bl-core.js — BrightLeaf Commerce runtime SDK.
 *
 * Public surface (semver'd, see CHANGELOG.md):
 *   window.BrightLeaf.events      pub/sub bus, also mirrored to DOM CustomEvents
 *   window.BrightLeaf.refreshSections  Section Rendering API helper
 *   window.BrightLeaf.ui          drawers, scroll lock, focus, live-region
 *   window.BrightLeaf.utils       money, debounce, throttle, media queries, motion
 *   window.BrightLeaf.components  component registry (register / mount / mounted)
 *   window.BrightLeaf.slots       app-extension slot registry
 *
 * Budget: < 10KB gzipped. Native ESM, zero dependencies, no build step.
 */

const VERSION = '0.8.0';

/* ------------------------------------------------------------------ events */

const listeners = new Map();

const events = {
  on(name, fn) {
    if (!listeners.has(name)) listeners.set(name, new Set());
    listeners.get(name).add(fn);
    return () => events.off(name, fn);
  },
  off(name, fn) {
    listeners.get(name)?.delete(fn);
  },
  once(name, fn) {
    const off = events.on(name, (detail) => {
      off();
      fn(detail);
    });
    return off;
  },
  emit(name, detail) {
    listeners.get(name)?.forEach((fn) => {
      try {
        fn(detail);
      } catch (error) {
        console.error(`[BrightLeaf] listener for "${name}" threw`, error);
      }
    });
    document.dispatchEvent(new CustomEvent(name, { detail, bubbles: true }));
  },
};

/* ------------------------------------------------------------------- utils */

function formatMoney(cents, currency = window.Shopify?.currency?.active || 'USD', locale = document.documentElement.lang || 'en') {
  return new Intl.NumberFormat(locale, { style: 'currency', currency }).format((Number(cents) || 0) / 100);
}

function debounce(fn, wait = 200) {
  let timer;
  return (...args) => {
    window.clearTimeout(timer);
    timer = window.setTimeout(() => fn(...args), wait);
  };
}

function throttle(fn, wait = 100) {
  let last = 0;
  let queued;
  return (...args) => {
    const now = Date.now();
    window.clearTimeout(queued);
    if (now - last >= wait) {
      last = now;
      fn(...args);
    } else {
      queued = window.setTimeout(() => {
        last = Date.now();
        fn(...args);
      }, wait - (now - last));
    }
  };
}

const prefersReducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/** Returns the resolved --bl-motion-duration in ms, honouring reduced motion. */
function motionDuration(el = document.documentElement) {
  if (prefersReducedMotion()) return 0;
  return parseInt(getComputedStyle(el).getPropertyValue('--bl-motion-duration'), 10) || 200;
}

/** Reactive media query: calls fn(matches) now and on every change. */
function media(query, fn) {
  const mql = window.matchMedia(query);
  const handler = (event) => fn(event.matches);
  fn(mql.matches);
  mql.addEventListener('change', handler);
  return () => mql.removeEventListener('change', handler);
}

const FOCUSABLE = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled]):not([type="hidden"])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  'summary',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

const focusable = (root) => [...root.querySelectorAll(FOCUSABLE)].filter((el) => el.offsetParent !== null || el === document.activeElement);

const utils = { money: formatMoney, debounce, throttle, prefersReducedMotion, motionDuration, media, focusable, FOCUSABLE };

/* ---------------------------------------------------------------------- ui */

let scrollLocks = 0;

function announce(message) {
  const region = document.querySelector('[data-bl-live-region]');
  if (!region) return;
  region.textContent = '';
  window.setTimeout(() => {
    region.textContent = message;
  }, 50);
}

const ui = {
  announce,
  /** Reference-counted so nested overlays cannot unlock the page early. */
  lockScroll() {
    scrollLocks += 1;
    document.documentElement.classList.add('bl-scroll-locked');
  },
  unlockScroll() {
    scrollLocks = Math.max(0, scrollLocks - 1);
    if (!scrollLocks) document.documentElement.classList.remove('bl-scroll-locked');
  },
  drawer(name) {
    return document.querySelector(`[data-bl-drawer="${name}"]`);
  },
  openDrawer(name) {
    ui.drawer(name)?.open?.();
  },
  closeDrawer(name) {
    ui.drawer(name)?.close?.();
  },
  closeAllDrawers() {
    document.querySelectorAll('[data-bl-drawer][open]').forEach((node) => node.close?.());
  },
};

/* -------------------------------------------------- section rendering API */

/**
 * Section Rendering API — re-render server-owned markup instead of reloading.
 * ids: array of { id, selector } where selector targets the DOM node to swap.
 */
async function refreshSections(ids = []) {
  if (!ids.length) return;
  const names = ids.map((entry) => entry.id).join(',');
  const response = await fetch(`${window.location.pathname}?sections=${encodeURIComponent(names)}`);
  const sections = await response.json();

  ids.forEach(({ id, selector }) => {
    const html = sections[id];
    if (!html) return;
    const target = document.querySelector(selector || `[data-bl-section="${id}"]`);
    if (!target) return;
    const parsed = new DOMParser().parseFromString(html, 'text/html');
    const source = parsed.querySelector(selector || `[data-bl-section="${id}"]`) || parsed.body.firstElementChild;
    if (source) target.replaceWith(source);
  });

  events.emit('bl:sections:rendered', { ids });
  components.mount();
}

/* -------------------------------------------------------- component registry */

/**
 * Components declare themselves in markup with data-bl-component="name" and
 * register a factory here. Nothing scans the DOM ad hoc, nothing runs twice,
 * and section re-renders (Theme Editor or Section Rendering API) re-mount
 * automatically.
 *
 *   BrightLeaf.components.register('header', (el, api) => {
 *     ...setup...
 *     return () => { ...teardown... };   // optional
 *   });
 */
const factories = new Map();
const instances = new WeakMap();

const componentApi = { events, ui, utils, slots: null };

const components = {
  register(name, factory) {
    factories.set(name, factory);
    components.mount(document, name);
    return () => factories.delete(name);
  },

  /** Mount every registered component found in root (idempotent). */
  mount(root = document, only = null) {
    const names = only ? [only] : [...factories.keys()];
    names.forEach((name) => {
      root.querySelectorAll(`[data-bl-component~="${name}"]`).forEach((el) => {
        const mounted = instances.get(el) || new Map();
        if (mounted.has(name)) return;
        try {
          mounted.set(name, factories.get(name)(el, componentApi) || true);
          instances.set(el, mounted);
          el.setAttribute('data-bl-mounted', '');
        } catch (error) {
          console.error(`[BrightLeaf] component "${name}" failed to mount`, error);
        }
      });
    });
    events.emit('bl:components:mounted', { root, only });
  },

  /** Run teardown callbacks for a subtree that is about to be removed. */
  unmount(root = document) {
    root.querySelectorAll('[data-bl-mounted]').forEach((el) => {
      instances.get(el)?.forEach((teardown) => {
        if (typeof teardown === 'function') teardown();
      });
      instances.delete(el);
      el.removeAttribute('data-bl-mounted');
    });
  },

  list() {
    return [...factories.keys()];
  },
};

/* ------------------------------------------------------------------- slots */

const slotRegistry = new Map();

const slots = {
  register(name, render) {
    slotRegistry.set(name, render);
    slots.render(name);
  },
  render(name) {
    document.querySelectorAll(`[data-bl-slot="${name}"]`).forEach((node) => {
      slotRegistry.get(name)?.(node, { events, ui, utils });
    });
  },
  list() {
    return [...document.querySelectorAll('[data-bl-slot]')].map((node) => node.dataset.blSlot);
  },
};

componentApi.slots = slots;

/* ----------------------------------------------------------------- context */

const customer = Object.freeze({
  id: window.__BL_CUSTOMER__?.id ?? null,
  tags: window.__BL_CUSTOMER__?.tags ?? [],
  isB2B: Boolean(window.__BL_CUSTOMER__?.isB2B),
});

/* ------------------------------------------------------- lifecycle wiring */

const mountAll = () => components.mount();

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', mountAll, { once: true });
} else {
  mountAll();
}

/* Theme Editor: sections are swapped in and out of the live DOM. */
document.addEventListener('shopify:section:load', (event) => components.mount(event.target));
document.addEventListener('shopify:section:unload', (event) => components.unmount(event.target));
document.addEventListener('shopify:block:select', (event) => events.emit('bl:block:select', { target: event.target }));

const BrightLeaf = Object.freeze({
  version: VERSION,
  events,
  ui,
  utils,
  components,
  slots,
  customer,
  refreshSections,
  /** @deprecated since 0.2.0 — use utils.money. Kept for companion-app compat. */
  format: { money: formatMoney },
  /** @deprecated since 0.2.0 — use ui.announce. */
  a11y: { announce },
});

window.BrightLeaf = BrightLeaf;
document.documentElement.classList.remove('no-js');
events.emit('bl:ready', { version: VERSION });

export default BrightLeaf;
export { events, ui, utils, components, slots, formatMoney, announce, refreshSections };
